import { Box, Button, CircularProgress, Flex, Image, Stack, Text, useToast } from "@chakra-ui/react";
import { useBlockNumber, useEthers } from "@usedapp/core";
import axios from "axios";
import BigNumber from "bignumber.js";
import { addWeeks, differenceInDays, differenceInHours, differenceInMinutes, isAfter, isBefore } from "date-fns";
import { head, keys, sumBy, values } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NftResource } from "../../../components/NftResource";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { LAYER_1_TOKENS } from "../../../core/constants/token";
import { useMarketplaceContract } from "../../../core/hooks/useContract";
import { useNft } from "../../../core/hooks/useNft";
import { IMoralisNft, INft } from "../../../core/models/nft";
import { ISale } from "../../../core/models/order";
import { Quote } from "../../../core/models/quote";
import { uriToHttps } from "../../../core/utils/ipfs";
import { formatNumber, toPlainString } from "../../../core/utils/number";
import { nftService } from "../../../services/nft.service";
import { useAppSelector } from "../../../store/hook";
import { UpcomingOrderDetailModal } from "./UpcomingOrderDetailModal";

interface Props {
  order: ISale;
  onRepaySuccess: () => void;
}

export const UpcomingOrder = React.memo((props: Props) => {
  const { library } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.PROFILE]);
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const token = useMemo(
    () => supportedTokens.byId[props.order.b_token_address],
    [props.order.b_token_address, supportedTokens]
  );
  const marketplaceContract = useMarketplaceContract();
  const toast = useToast();
  const blockNumber = useBlockNumber();

  const [isPaying, setIsPaying] = useState(false);
  const [repayError, setRepayError] = useState<string>();

  const [nft, setNft] = useState<IMoralisNft>();

  const [isVisibleDetail, setIsVisibleDetail] = useState(false);
  const [now, setNow] = useState<number>();

  useEffect(() => {
    async function getDetail() {
      const nftResponse = await nftService.getNftDetail(
        props.order.asset_transfer.token_address,
        props.order.asset_transfer.token_id
      );

      const uri = nftResponse.data.token_uri ? uriToHttps(nftResponse.data.token_uri) : undefined;
      const metadataResponse = uri ? await axios.get(uri) : undefined;

      setNft({
        ...nftResponse.data,
        metadata: metadataResponse?.data,
      });
    }

    getDetail();
  }, []);

  useEffect(() => {
    async function getNow() {
      if (!blockNumber) return;
      const block = await library?.getBlock(blockNumber);
      const now = (block?.timestamp || 0) * 1000;
      setNow(now);
    }

    getNow();
  }, [blockNumber]);

  const paidAmount = useMemo(() => sumBy(values(props.order.payments), "payment_amount"), [props.order.payments]);

  const remainingAmount = useMemo(() => props.order.price - paidAmount, [props.order.price, paidAmount]);
  const paymentPeriods = useMemo(
    () => Math.ceil(props.order.price / Math.ceil(props.order.price / 4)),
    [props.order.price]
  );

  const paidPeriods = useMemo(() => keys(props.order.payments).length, [props.order.payments]);
  const currentPeriod = useMemo(() => paidPeriods + 1, [paidPeriods]);
  const dueDate = useMemo(
    () =>
      head(values(props.order.payments)) && paidPeriods > 0
        ? addWeeks(head(values(props.order.payments))?.payment_date || new Date(), 2 * paidPeriods)
        : null,
    [props.order]
  );

  const onCloseDetail = () => {
    setIsVisibleDetail(false);
  };

  const onRepay = async () => {
    try {
      setIsPaying(true);
      setRepayError(undefined);
      const onChainQuote = await Quote.toOnChainQuote(props.order.quote);

      const isLayer1Token = LAYER_1_TOKENS.includes(token.underlying);
      const tx = await marketplaceContract?.repay(onChainQuote, {
        value: isLayer1Token ? toPlainString(props.order.price - remainingAmount) : 0,
      });
      await tx.wait();

      toast({
        title: "Success",
        description: `${t("You successfully paid for")} ${nft?.metadata?.name}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      onCloseDetail();
      setTimeout(() => {
        props.onRepaySuccess();
      }, 1500);
    } catch (e: any) {
      setRepayError(e?.data?.message || e?.message);
    } finally {
      setIsPaying(false);
    }
  };

  const renderDueDate = useCallback(() => {
    if (!dueDate || !now) return;

    if (isBefore(now, dueDate)) {
      const diffInDays = differenceInDays(dueDate, now);
      const diffInHours = differenceInHours(dueDate, now);
      const diffInMinutes = differenceInMinutes(dueDate, now);
      return (
        <Text fontSize={"small"} color={"#A6A6A6"} fontWeight={500}>
          {t("Due in", {
            day: diffInDays,
            hour: diffInHours - diffInDays * 24,
            minute: diffInMinutes - diffInHours * 60,
          })}
        </Text>
      );
    }

    if (isAfter(dueDate, now))
      return (
        <Text fontSize={"small"} color={"red.500"} fontWeight={500}>
          {t("days overdue", { day: differenceInDays(dueDate, now) })}
        </Text>
      );
  }, [dueDate, now]);

  return (
    <>
      <Flex
        marginTop={"16px"}
        alignItems={"center"}
        borderWidth={1}
        padding={"12px 20px"}
        borderRadius={"xl"}
        gridGap={"20px"}
      >
        <Flex position={"relative"} height={"50px"} width={"50px"} alignItems={"center"} justifyContent={"center"}>
          <CircularProgress
            height={"50px"}
            width={"50px"}
            value={(100 / paymentPeriods) * (currentPeriod - 1)}
            position={"absolute"}
            top={0}
            left={0}
          />
          <Text fontWeight={700}>{currentPeriod - 1}</Text>
        </Flex>

        <Box width={"60px"} height={"60px"}>
          <NftResource image={nft?.metadata?.image} animationUrl={nft?.metadata?.animation_url} muted />
        </Box>

        <Box flex={1}>
          <Text fontWeight={"700"}>{nft?.metadata?.name}</Text>

          <Flex fontSize={"small"} alignItems={"center"}>
            <Text>{t("Remaining")}:</Text>
            <Image src={token?.logoUrl} height="18px" width="18px" marginRight={"4px"} marginLeft={"6px"} />
            <Text fontWeight={700} color={colors.primary1} fontSize={"md"}>
              {formatNumber(new BigNumber(remainingAmount).div(Math.pow(10, token.decimals)).toNumber())}
            </Text>
          </Flex>
        </Box>

        <Stack alignItems={"flex-end"}>
          <Button
            colorScheme={"blue"}
            onClick={() => setIsVisibleDetail(true)}
            isLoading={isPaying}
            width={"fit-content"}
            padding={"0px 30px"}
          >
            {t("Pay")}
          </Button>
          {renderDueDate()}
        </Stack>
      </Flex>
      <UpcomingOrderDetailModal
        order={props.order}
        isVisible={isVisibleDetail}
        onClose={onCloseDetail}
        nft={nft}
        token={token}
        onPay={onRepay}
        isPaying={isPaying}
        repayError={repayError}
      />
    </>
  );
});
